import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useDropzone } from 'react-dropzone';
import {
  CloudArrowUpIcon,
  LockClosedIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { publicLinksApi } from '../api/public-links';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ProgressBar } from '../components/ui/ProgressBar';
import { Spinner } from '../components/ui/Spinner';
import { formatBytes } from '../utils/dateFormat';

interface UploadItem {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

export function PublicUploadPage() {
  const { t } = useTranslation('public');
  const { token } = useParams<{ token: string }>();
  const [linkInfo, setLinkInfo] = useState<{
    folderName?: string;
    hasPassword: boolean;
    isExpired: boolean;
    remainingUploads: number | null;
  } | null>(null);
  const [password, setPassword] = useState('');
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadInfo = async () => {
      if (!token) return;
      try {
        const info = await publicLinksApi.getInfo(token);
        if (info.linkType !== 'upload') {
          setError(t('upload.notForUploading'));
        } else {
          setLinkInfo(info);
        }
      } catch {
        setError(t('upload.linkNotFoundDesc'));
      } finally {
        setIsLoading(false);
      }
    };
    loadInfo();
  }, [token, t]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newUploads = acceptedFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      progress: 0,
      status: 'pending' as const,
    }));
    setUploads((prev) => [...prev, ...newUploads]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploading || linkInfo?.isExpired,
  });

  const uploadFile = async (upload: UploadItem) => {
    if (!token) return;

    setUploads((prev) =>
      prev.map((u) => (u.id === upload.id ? { ...u, status: 'uploading' } : u))
    );

    try {
      const { uploadUrl, fileId } = await publicLinksApi.upload(token, {
        fileName: upload.file.name,
        mimeType: upload.file.type || 'application/octet-stream',
        size: upload.file.size,
        password: password || undefined,
      });

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploads((prev) =>
              prev.map((u) => (u.id === upload.id ? { ...u, progress } : u))
            );
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', upload.file.type || 'application/octet-stream');
        xhr.send(upload.file);
      });

      await publicLinksApi.confirmUpload(token, fileId);

      setUploads((prev) =>
        prev.map((u) =>
          u.id === upload.id ? { ...u, status: 'completed', progress: 100 } : u
        )
      );
    } catch (err: any) {
      setUploads((prev) =>
        prev.map((u) =>
          u.id === upload.id
            ? { ...u, status: 'error', error: err.message || 'Upload failed' }
            : u
        )
      );
    }
  };

  const handleUpload = async () => {
    const pendingUploads = uploads.filter((u) => u.status === 'pending');
    if (pendingUploads.length === 0) return;

    setIsUploading(true);
    setError('');

    for (const upload of pendingUploads) {
      await uploadFile(upload);
    }

    setIsUploading(false);
  };

  const removeUpload = (id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && !linkInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <XMarkIcon className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('upload.linkNotAvailable')}</h1>
          <p className="text-gray-500 dark:text-slate-400">{error}</p>
        </motion.div>
      </div>
    );
  }

  const pendingCount = uploads.filter((u) => u.status === 'pending').length;
  const completedCount = uploads.filter((u) => u.status === 'completed').length;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 max-w-lg w-full"
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl shadow-lg mb-4">
            <CloudArrowUpIcon className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('upload.title')}</h1>
          {linkInfo?.folderName && (
            <p className="text-gray-500 dark:text-slate-400 mt-1">{t('upload.toFolder', { folder: linkInfo.folderName })}</p>
          )}
        </div>

        {/* Password input */}
        {linkInfo?.hasPassword && (
          <div className="mb-6">
            <Input
              type="password"
              label={t('upload.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('upload.enterPassword')}
              leftIcon={<LockClosedIcon className="w-5 h-5" />}
            />
          </div>
        )}

        {/* Drop zone */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer mb-4 transition-all duration-150 ${
            isDragActive
              ? 'scale-[1.02] border-primary-500 bg-primary-50 dark:bg-primary-900/30'
              : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700'
          }`}
        >
          <input {...getInputProps()} />
          <CloudArrowUpIcon className="w-10 h-10 mx-auto text-gray-400 dark:text-slate-500 mb-3" />
          {isDragActive ? (
            <p className="text-primary-600 dark:text-primary-400 font-medium">{t('upload.dropHere')}</p>
          ) : (
            <>
              <p className="text-gray-700 dark:text-slate-200 font-medium">{t('upload.dragAndDrop')}</p>
              <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">{t('upload.orClickToSelect')}</p>
            </>
          )}
        </div>

        {/* Upload list */}
        <AnimatePresence>
          {uploads.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="max-h-48 overflow-y-auto space-y-2 mb-4"
            >
              {uploads.map((upload) => (
                <motion.div
                  key={upload.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{upload.file.name}</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">{formatBytes(upload.file.size)}</p>
                    </div>
                    {upload.status === 'completed' ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    ) : upload.status !== 'uploading' ? (
                      <button
                        onClick={() => removeUpload(upload.id)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                      >
                        <XMarkIcon className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                      </button>
                    ) : null}
                  </div>
                  <ProgressBar progress={upload.progress} status={upload.status} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upload button */}
        {pendingCount > 0 && (
          <Button
            onClick={handleUpload}
            isLoading={isUploading}
            disabled={linkInfo?.isExpired}
            className="w-full"
            size="lg"
          >
            {t('upload.uploadCount', { count: pendingCount })}
          </Button>
        )}

        {completedCount > 0 && pendingCount === 0 && (
          <div className="text-center">
            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
            <p className="text-green-600 font-medium">
              {t('upload.successCount', { count: completedCount })}
            </p>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 dark:text-slate-500 mt-6">
          {t('upload.sharedVia')}
        </p>
      </motion.div>
    </div>
  );
}
